/**
 * order-summary.ts — Nội dung + nút đi tiếp của trang kết quả thanh toán (/billing/result) và khung theo dõi đơn ở /billing
 * (hàm thuần, có test). Dùng chung cho mọi loại đơn: mua / đổi gói, mua thêm, nạp credit AI.
 */
import type { BillingOrder } from '@/hooks/use-billing'
import { formatVnd } from '@/lib/format-money'
import { formatTurns } from '@/lib/ai-credits'
import { i18n } from '@/lib/i18n'
import {
  addonDeltaText, isAddonOrder, isAiCreditOrder, resolvePlan, termLabel, vatBreakdown, vatLabel,
} from './plans'

export type OrderKind = 'plan' | 'addon' | 'ai'

export function orderKindOf(o: Pick<BillingOrder, 'cycle' | 'planId' | 'quote'>): OrderKind {
  return isAiCreditOrder(o) ? 'ai' : isAddonOrder(o) ? 'addon' : 'plan'
}

/** Tiêu đề + mô tả khi đơn đã thanh toán ("Thanh toán thành công 5.019.300 đ (đã gồm VAT 10%)", "Gói … đã được kích hoạt, hiệu lực đến …"). */
export function paidSummary(order: BillingOrder): { title: string; desc: string } {
  const vb = vatBreakdown(order.quote, order.amount)
  const title = vb && vb.amount > 0
    ? i18n.t('billing:orderSummary.paidTitleVat', { amount: formatVnd(order.amount), vat: vatLabel(vb.percent) })
    : i18n.t('billing:orderSummary.paidTitle', { amount: formatVnd(order.amount) })
  const until = order.periodEnd ? i18n.t('billing:orderSummary.until', { date: new Date(order.periodEnd).toLocaleDateString('vi-VN') }) : ''
  const planName = order.quote?.planName ?? resolvePlan(order.planId).name
  const c = order.quote?.aiCredits
  const desc = isAiCreditOrder(order)
    ? c?.turns ? i18n.t('billing:orderSummary.aiCreditAdded', { turns: formatTurns(c.turns) }) : i18n.t('billing:orderSummary.aiCreditAddedGeneric')
    : isAddonOrder(order)
      ? i18n.t('billing:orderSummary.addonAdded', { delta: addonDeltaText(order.quote?.addon?.delta) || i18n.t('billing:orderSummary.quantity'), plan: planName, until })
      : i18n.t('billing:orderSummary.planActivated', { plan: planName, term: termLabel(order.days), until })
  return { title, desc }
}

export interface ResultLink { label: string; to: string }

const BILLING_HOME: ResultLink = { get label() { return i18n.t('billing:orderSummary.billingHome') }, to: '/billing' }

/**
 * Nút đi tiếp khi ĐÃ thanh toán: một nút chính duy nhất + nút phụ.
 *  - Có trang đang làm dở (`from`): "Tiếp tục việc đang làm".
 *  - Không có: mua / đổi gói → Tổng quan; mua thêm → trang gói; nạp credit → trang AI.
 */
export function paidLinks(kind: OrderKind, from: string | null): { primary: ResultLink; secondary: ResultLink[] } {
  if (from) return { primary: { label: i18n.t('billing:orderSummary.resumeWork'), to: from }, secondary: [BILLING_HOME] }
  if (kind === 'ai') return { primary: { label: i18n.t('billing:orderSummary.openAiPage'), to: '/ai' }, secondary: [BILLING_HOME] }
  if (kind === 'addon') return { primary: { label: i18n.t('billing:orderSummary.viewCurrentPlan'), to: '/billing' }, secondary: [{ label: i18n.t('billing:orderSummary.backToDashboard'), to: '/dashboard' }] }
  return { primary: { label: i18n.t('billing:orderSummary.backToDashboard'), to: '/dashboard' }, secondary: [BILLING_HOME] }
}

/** Nút "chọn lại" khi đơn không thành công (ngoài nút "Thử lại" / "Tạo mã mới" của OrderActions): về đúng form đã đặt. */
export function retryLink(order: Pick<BillingOrder, 'cycle' | 'planId' | 'quote' | 'days'>, from: string | null): ResultLink {
  const kind = orderKindOf(order)
  if (kind === 'addon') return { label: i18n.t('billing:orderSummary.reselectQuantity'), to: '/billing/addon' }
  if (kind === 'ai') return { label: from ? i18n.t('billing:orderSummary.backToPrevious') : i18n.t('billing:orderSummary.backToPlansPage'), to: from ?? '/billing' }
  return { label: i18n.t('billing:orderSummary.reselectPlan'), to: `/billing/upgrade?plan=${encodeURIComponent(order.planId)}&days=${order.days}` }
}
